// frontend/src/contexts/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../services/api";
import type { User } from "../services/api";

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  updateUserPermissions: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Verify authentication on mount and get user data
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await api.get("/auth/me");
        if (response.data.success) {
          setUser(response.data.data);
        } else {
          setUser(null);
        }
      } catch (error) {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (
    username: string,
    password: string
  ): Promise<boolean> => {
    const response = await api.post("/auth/login", {
      username,
      password,
    });

    if (response.data.success) {
      setUser(response.data.data.user);
      return true;
    }
    return false;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
    }
  };

  const updateUserPermissions = async (): Promise<User | null> => {
    try {
      const response = await api.get("/auth/me");
      if (response.data.success) {
        const updatedUser = response.data.data as User;
        setUser(updatedUser);
        return updatedUser;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const contextValue: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: user !== null,
    isLoading,
    updateUserPermissions,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export type { User, AuthContextType };
